/**
 * @fileoverview Helper class for import/export functionality.
 * Handles parsing, validation, and conversion of data for import/export operations.
 * @module helpers/ImportExportHelper
 */

import { ImportDialog } from '../ui/ImportDialog.js';
import { ExportDialog } from '../ui/ExportDialog.js';
import { PdfHighlightStore } from '../ui/PdfHighlightStore.js';
import { sanitizeStoredUiState, sanitizeStoredMusicList, STORAGE_KEY_SEGMENTS_PATTERN } from '../state/StateManager.js';
import { createAnnotatedReturnVal, getOwnValue, normalizeCssColorToHex } from '../utils/jrfuncs.js';
import { formatAddressAptWithComma, formatJregionLoclabelWithAt } from '../utils/nynfuncs.js';
import { PTYPES, CUSTOM_ITEM_FIELDS_ALL, SEARCH_TYPES, FIELD_ISCUSTOMIZED, FIELD_ISBOOKMARK, MAP_CUSTOMS } from '../constants.js';
import { getDefaultLeadIdPatterner } from '../utils/LeadIdPatterner.js';

// Import validation of custom bookmark fields (see _checkListItemField)
const IMPORT_MAX_UID_LENGTH = 1000;
// the only colour form kept (what the bookmark dialog's colour input takes; normalizeCssColorToHex
// turns short hex, 8-digit hex, colour names and color(srgb ...) into it first)
const IMPORT_COLOR_REGEX = /^#[0-9a-f]{6}$/;
// Import validation of search / pdf-lead / custom-pin items (see _checkListItem)
const IMPORT_MAX_TEXT_LENGTH = 1000;        // a label, lead, display name or address line
const IMPORT_MAX_QUERY_LENGTH = 5000;       // a search query (advanced queries can be long)
const IMPORT_MAX_NOTES_LENGTH = 100000;     // a bookmark's free-text notes
// a region id (the worlds use short codes like "MH", "NW", "D"); used when the current world's own
// region list is not available to check against (e.g. a settings backup covering several worlds)
const IMPORT_REGION_ID_REGEX = /^[A-Za-z0-9_-]{1,20}$/;
// the searchers a saved / history search can be re-run on
const IMPORT_SEARCH_TYPES = [SEARCH_TYPES.WHITE, SEARCH_TYPES.YELLOW, SEARCH_TYPES.REVERSE, SEARCH_TYPES.ADVANCED];
// settings backup: the app-scoped keys (after the app prefix) and the world-scoped key suffixes
// ("<app prefix><world prefix>[_<case>]_<suffix>") this app writes; anything else is refused. The
// "<world prefix>[_<case>]" part is StateManager's shared rule (main.js checks ?keystore= with it).
const BACKUP_WORLD_KEY_REGEX = new RegExp('^(' + STORAGE_KEY_SEGMENTS_PATTERN + ')_(ui|bookmarks|history|savedsearch)$');
const BACKUP_WORLD_ID_REGEX = /^[A-Za-z0-9_.-]{1,100}$/;
// how many left-out values per backup entry the confirmation lists by name before summarizing
const BACKUP_MAX_REPORTED_PER_KEY = 20;

// The fields a list item that is NOT a database POI carries, exactly as the app itself writes them:
// a saved or history search (IntSearcher.addSavedSearchItemManually), a pdf-lead bookmark
// (addPdfLeadItemManually), a custom map pin (MapApp.createCustomUserBookmarkAtLocation) and a POI
// stub whose POI is no longer in the database. Such items are stored whole (MapApp.
// createItemCustomStubForSerializing leaves them alone), so a settings backup restore and the import
// dialog must keep every one of these fields, unchanged, whenever its value passes its check in
// _checkListItemField; anything else on the item is left out and reported. A field added to the items
// (or to CUSTOM_ITEM_FIELDS_*) needs an entry here AND a check there, or a restore silently loses it.
const LIST_ITEM_COMMON_FIELDS = [
    // the user's customisation (BookmarkDialog -> MapApp.doSaveEditedItem) and the two timestamps
    ...CUSTOM_ITEM_FIELDS_ALL,
    // flags the app keeps on the item (isCustomized is derived from the user fields, see
    // MapApp.updateItemIsCustomized; the custom pin style and the lister read it)
    FIELD_ISCUSTOMIZED, FIELD_ISBOOKMARK,
    // display text (the curated "how to find it" searches carry dName / dNameCustom)
    'label', 'dName', 'dNameCustom', 'customBase',
];
const LIST_ITEM_SEARCH_FIELDS = ['searchQuery', 'searchType', 'searchTotalCount', 'selectedRegion', 'regionCheckbox'];
const LIST_ITEM_KIND_FIELDS = {
    [PTYPES.SEARCH]: LIST_ITEM_SEARCH_FIELDS,
    [PTYPES.PDFLEAD]: ['lead', 'address', ...LIST_ITEM_SEARCH_FIELDS],
    [PTYPES.CUSTOM]: ['lead', 'address', 'loclabel', 'jregion', 'pcat', 'pcatg', 'geometry'],
    [PTYPES.POISTUB]: [],
};



/**
 * Helper class for handling import and export operations.
 * Manages data parsing, validation, and format conversion.
 * @class
 */
export class ImportExportHelper {
    /**
     * Creates a new ImportExportHelper instance.
     * @param {MapApp} mapApp - Reference to the main MapApp instance
     */
    constructor(mapApp) {
        this.mapApp = mapApp;
    }


    /**
     * Gets the PoiDataManager from MapApp.
     * @returns {PoiDataManager} The POI data manager
     * @private
     */
    getPoiDataManager() {
        return this.mapApp.getPoiDataManager();
    }


    /**
     * Gets a searcher by name from MapApp.
     * @param {string} tabName - The searcher name
     * @returns {Searcher} The searcher instance
     * @private
     */
    getSearcherByName(tabName) {
        return this.mapApp.getSearcherByName(tabName);
    }




    // ==========================================
    // Import / Export stuff
    // ==========================================


    /**
     * Parse text (which might be json or plaintext) and prepare to import it
     * If flagJustValidate is set, then dont do any actual importing, just validate
     * Otherwise DO the actual import
     * @param {string} tabName - Target tab/searcher name for import
     * @param {string} label - Display label for diagnostic messages
     * @param {string} text - Raw text content to parse
     * @param {boolean} flagJustValidate - If true, only validate without importing
     * @returns {Object} Result object with {results, diagnosticsMessage, success}
     */
    parseTextForImport(tabName, label, text, flagJustValidate) {
        // get searcher that will take this import
        const searcher = this.getSearcherByName(tabName);

        // parse the text into results
        let resultsWithMessage = this.parseTextForImportGenerateResults(searcher, label, text);
        if (!resultsWithMessage.success) {
            return resultsWithMessage;
        }

        // validate the parsed results for this searcher
        resultsWithMessage = this.validateImportResultsForSearcher(resultsWithMessage.results, searcher, label);
        if (!resultsWithMessage.success) {
            return resultsWithMessage;
        }

        // if just validating, return with instruction to import
        if (flagJustValidate) {
            return createAnnotatedReturnVal(
                resultsWithMessage.results,
                resultsWithMessage.diagnosticsMessage + '\nClick the Import button to perform the actual import.',
                true
            );
        }

        // DO the actual import
        return this.importResultsIntoSearcher(searcher, resultsWithMessage.results, resultsWithMessage.importedFieldsByItem);
    }



    /**
     * Imports validated results into a searcher.
     * @param {Searcher} searcher - The searcher to import into
     * @param {Array<Object>} results - Array of validated result items to import
     * @param {Map<Object, Object>} [importedFieldsByItem] - a live POI's custom fields from the file
     *   (validateImportResultsForSearcher), applied here and nowhere earlier
     * @returns {Object} Result object with {results, diagnosticsMessage, success}
     */
    importResultsIntoSearcher(searcher, results, importedFieldsByItem = null) {
        // loop and add (removing duplicates!)
        const resultCount = results.length;
        // Preserve the file's order in the resulting list. For "add new to top" searchers (history,
        // bookmarks, saved searches) addResultRef unshifts each item to the top, so adding in forward
        // order would reverse the list; iterate in reverse so the first file entry ends up on top.
        const orderedResults = searcher.addNewToTop ? results.slice().reverse() : results;
        for (const result of orderedResults) {
            // addResultRef first removes the list's item with the same uid -- for a POI already on the
            // list that is THIS object, and the bookmark searcher's removal clears its custom fields --
            // and then stamps the current time on it: put back the user fields it already had and,
            // over them, the ones and the time the import brought, so an imported bookmark keeps them
            // (a live POI's fields from the file arrive in importedFieldsByItem; any other item --
            // a search, a custom pin, a pdf lead -- is a fresh object that carries its own)
            const importedFields = {};
            const fromFile = importedFieldsByItem ? importedFieldsByItem.get(result) : undefined;
            for (const source of (fromFile ? [result, fromFile] : [result])) {
                for (const field of CUSTOM_ITEM_FIELDS_ALL) {
                    if (source[field] !== undefined) {
                        importedFields[field] = source[field];
                    }
                }
            }
            // ATTN: 1/30/26 now we add a REFERENCE not a copy!
            const added = searcher.addResultRef(result, 'Importing items');
            if (added) {
                Object.assign(added, importedFields);
                this.mapApp.updateItemIsCustomized(added);
            }
        }
        // now force refresh and state save
        searcher.getMapApp().displayNeedsRefresh_AfterBulkImport(searcher);
        searcher.saveResultsToStorage();
        return createAnnotatedReturnVal(null, `Imported ${resultCount} entries.`, true);
    }




    /**
     * Parses text content and generates import results.
     * Attempts JSON parsing first, falls back to regex pattern matching.
     * @param {Searcher} searcher - The target searcher for the import
     * @param {string} label - Label for diagnostic messages
     * @param {string} text - Raw text content to parse
     * @returns {Object} Result object with {results: Array, diagnosticsMessage: string, success: boolean}
     */
    parseTextForImportGenerateResults(searcher, label, text) {
        // Try JSON parsing first
        let jsonData;
        try {
            jsonData = JSON.parse(text);
        } catch (jsonError) {
            // Not JSON data, try regex-based parsing
            const resultsWithMessage = this.parseTextForImportGenerateResultsCaseBookOrSimpleRegex(searcher, text);
            if (!resultsWithMessage.success) {
                resultsWithMessage.diagnosticsMessage += `; JSON parse error was ${jsonError.message}.`;
            }
            return resultsWithMessage;
        }

        // Valid JSON. The standard export format is a plain array of items, but a GeoJSON
        // FeatureCollection (the POI database format; also what the case "dbuserleads" export
        // writes) is accepted too, and any other JSON shape is walked for objects carrying a
        // "lead". Only when none of that yields items does the text get the same lead-id scan
        // as non-JSON text (which cannot see quote-delimited ids, so it is the last resort);
        // it used to hand the validator a non-array and crash with "is not iterable".
        const normalized = this._normalizeJsonImportData(jsonData);
        if (normalized === null) {
            const resultsWithMessage = this.parseTextForImportGenerateResultsCaseBookOrSimpleRegex(searcher, text);
            if (!resultsWithMessage.success) {
                resultsWithMessage.diagnosticsMessage += '; the JSON was neither an array of items nor a GeoJSON FeatureCollection, and no object in it carries a "lead".';
            }
            return resultsWithMessage;
        }

        const { results, sourceLabel, skippedCount } = normalized;
        let message = `${sourceLabel} successfully parsed (found ${results.length} items).`;
        if (skippedCount > 0) {
            message += ` Ignored ${skippedCount} entr${skippedCount === 1 ? 'y' : 'ies'} that ${skippedCount === 1 ? 'was' : 'were'} not an item object.`;
        }
        return createAnnotatedReturnVal(results, message, true);
    }


    /**
     * Turns parsed JSON into the flat array of item objects the validator expects.
     * Accepts the standard export array, a GeoJSON FeatureCollection (each feature's
     * `properties` bag is the item), bare lead-id strings inside an array, and as a
     * fallback any other object, walked for nested objects that carry a "lead".
     * @param {*} jsonData - Whatever JSON.parse returned
     * @returns {{results: Array<Object>, sourceLabel: string, skippedCount: number}|null}
     *   null when the JSON has no shape we know how to walk
     * @private
     */
    _normalizeJsonImportData(jsonData) {
        let rawItems;
        let sourceLabel;
        if (Array.isArray(jsonData)) {
            rawItems = jsonData;
            sourceLabel = 'Data';
        } else if (jsonData && typeof jsonData === 'object' && jsonData.type === 'FeatureCollection' && Array.isArray(jsonData.features)) {
            rawItems = jsonData.features;
            sourceLabel = 'GeoJSON FeatureCollection';
        } else if (jsonData && typeof jsonData === 'object') {
            rawItems = this._collectLeadObjectsFromJson(jsonData);
            if (rawItems.length === 0) {
                return null;
            }
            sourceLabel = 'JSON (scanned for objects with a lead)';
        } else {
            return null;
        }

        const results = [];
        let skippedCount = 0;
        for (const raw of rawItems) {
            if (typeof raw === 'string') {
                // a bare lead id
                const lead = raw.trim();
                if (lead) {
                    results.push({ lead });
                } else {
                    skippedCount++;
                }
            } else if (raw && typeof raw === 'object') {
                if (raw.type === 'Feature' && raw.properties && typeof raw.properties === 'object') {
                    // GeoJSON feature: the item fields live in its properties bag
                    results.push(raw.properties);
                } else {
                    results.push(raw);
                }
            } else {
                skippedCount++;
            }
        }
        return { results, sourceLabel, skippedCount };
    }


    /**
     * Depth-first walk of an arbitrary JSON value collecting every object that has a
     * string "lead" (a GeoJSON Feature contributes its properties bag). A collected object
     * is not descended into, and a lead seen twice keeps its first object.
     * @param {*} value - Parsed JSON value to walk
     * @returns {Array<Object>} Lead-bearing objects in encounter order
     * @private
     */
    _collectLeadObjectsFromJson(value) {
        const found = [];
        const seenLeads = new Set();
        const walk = (node, depth) => {
            if (!node || typeof node !== 'object' || depth > 32) {
                return;
            }
            if (Array.isArray(node)) {
                for (const child of node) {
                    walk(child, depth + 1);
                }
                return;
            }
            const candidate = (node.type === 'Feature' && node.properties && typeof node.properties === 'object') ? node.properties : node;
            if (typeof candidate.lead === 'string' && candidate.lead.trim()) {
                const lead = candidate.lead.trim();
                if (!seenLeads.has(lead)) {
                    seenLeads.add(lead);
                    found.push(candidate);
                }
                return;
            }
            for (const child of Object.values(node)) {
                walk(child, depth + 1);
            }
        };
        walk(value, 0);
        return found;
    }


    /**
     * Parses text using Casebook header or lead ID regex patterns.
     * Extracts lead IDs from "## X-XXXX" headers or standalone lead patterns.
     * @param {Searcher} searcher - The target searcher (unused but kept for consistency)
     * @param {string} text - Text content to scan for patterns
     * @returns {Object} Result object with {results: Array, diagnosticsMessage: string, success: boolean}
     */
    parseTextForImportGenerateResultsCaseBookOrSimpleRegex(searcher, text) {
        const leadData = {};
        const lines = text.split('\n');

        // Pre-pass: collect "reserved" ids declared via $reserveLead("…") or
        // $reserveLead(id="…"). The quoted string may contain a comma-
        // separated list (e.g. $reserveLead("1-2345,2-3456")). Reserved ids
        // are suppressed in the loose lead-id scan below so that incidental
        // mentions elsewhere in the text don't add spurious leads. (Casebook
        // chapter headers are explicit declarations and are NOT suppressed.)
        const reservedLeads = new Set();
        const reserveLeadPattern = /\$reserveLead\(\s*(?:id\s*=\s*)?"([^"]*)"\s*\)/g;
        for (const line of lines) {
            for (const m of line.matchAll(reserveLeadPattern)) {
                for (const piece of m[1].split(',')) {
                    const trimmed = piece.trim();
                    if (trimmed) reservedLeads.add(trimmed);
                }
            }
        }

        // regex for casebook
        const regexPatternCasebookHeader = /^##\s+(\d-\d{4})(?:\s+"([^"]*)")?.*$/;

        // First pass: check for regex pattern 1
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const lineNo = i + 1;
            const match = line.match(regexPatternCasebookHeader);
            if (match) {
                const id = match[1];
                if (reservedLeads.has(id)) continue;
                if (!leadData[id]) {
                    leadData[id] = {
                        id: id,
                        label: match[2],
                        lineNo: lineNo
                    };
                }
            }
        }

        // Second pass: check for lead id regex, ignore if id already exists
        // or has been reserved via $reserveLead.
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const lineNo = i + 1;
            const matches = getDefaultLeadIdPatterner().findAllIdPatternsInLine(line)
            for (const match of matches) {
                const id = match;
                if (reservedLeads.has(id)) continue;
                if (!leadData[id]) {
                    leadData[id] = {
                        id: id,
                        lineNo: lineNo
                    };
                }
            }
        }



        // Create a results array from leadData, with 'lead' set to id and 'label' if available
        const results = Object.values(leadData).map(item => ({
            lead: item.id,
            label: item.label,
            lineNo: item.lineNo
        }));

        const success = results.length > 0;
        const diagnosticsMessage = success
            ? `Found ${results.length} matches.`
            : 'No matches found for regex patterns.';

        return createAnnotatedReturnVal(results, diagnosticsMessage, success);
    }





    /**
     * Validates import results against searcher requirements.
     * Checks if results are POI items or search items and validates accordingly.
     * @param {Array<Object>} results - Array of parsed results to validate
     * @param {Searcher} searcher - The target searcher with type restrictions
     * @param {string} label - Label for diagnostic messages
     * @returns {Object} Result object with {results: Array, diagnosticsMessage: string, success: boolean}
     */
    validateImportResultsForSearcher(results, searcher, label) {
        const allowsPoi = searcher.doesAllowResultTypePoi();
        const allowsSearch = searcher.doesAllowResultTypeSearch();

        const validatedResults = [];
        const errors = [];
        // values left out of items that are imported (an invalid or unknown field)
        const warnings = [];

        // the current world's region ids, which an imported search's selectedRegion must be one of
        const allowedRegionIds = this._currentWorldRegionIds();
        // a live POI's custom fields from the file, kept apart until the import really happens
        // (_validatePoiItem); one POI listed twice in a file gets both lines' fields, the later winning
        const importedFieldsByItem = new Map();

        // validate each result
        for (const result of results) {
            const validation = this._validateSingleImportResult(result, allowsPoi, allowsSearch, allowedRegionIds);
            if (validation.valid) {
                validatedResults.push(validation.result);
                if (validation.importedFields) {
                    importedFieldsByItem.set(validation.result, Object.assign(importedFieldsByItem.get(validation.result) || {}, validation.importedFields));
                }
                for (const { field, reason } of (validation.droppedFields || [])) {
                    warnings.push(`${validation.description}: left out "${this._previewText(field)}" (${reason}).`);
                }
            } else {
                errors.push(validation.error);
            }
        }

        const diagnostics = this._buildValidationDiagnostics(validatedResults, errors, label, warnings);
        diagnostics.importedFieldsByItem = importedFieldsByItem;
        return diagnostics;
    }


    /**
     * Validates a single import result item.
     * @param {Object} result - The result item to validate
     * @param {boolean} allowsPoi - Whether POI items are allowed
     * @param {boolean} allowsSearch - Whether search items are allowed
     * @param {string[]|null} allowedRegionIds - The current world's region ids (null = check the format only)
     * @returns {Object} Validation result with {valid, result?, error?, description?, droppedFields?}
     *   (droppedFields: the values left out of an accepted item, each {field, reason})
     * @private
     */
    _validateSingleImportResult(result, allowsPoi, allowsSearch, allowedRegionIds) {
        if (!result || typeof result !== 'object' || Array.isArray(result)) {
            return { valid: false, error: 'Not an item object.' };
        }

        // Check if this is a search item
        if (result.ptype === PTYPES.SEARCH) {
            return this._validateSearchItem(result, allowsSearch, allowedRegionIds);
        }

        // a pdf lead lives where POI items do (history, bookmarks), not among the saved searches
        if (result.ptype === PTYPES.PDFLEAD) {
            return this._validatePdfLead(result, allowsPoi, allowedRegionIds);
        }

        // a custom map pin (its lead is the placeholder "user", which no POI has)
        if (result.ptype === PTYPES.CUSTOM) {
            return this._validateCustomPin(result, allowsPoi);
        }

        // Check if this is a POI item (has a lead value)
        if (result.lead) {
            return this._validatePoiItem(result, allowsPoi);
        }

        // Unknown result type
        return { valid: false, error: 'Unknown result type (no lead or type field).' };
    }


    /**
     * Validates a search-type import item.
     * @param {Object} result - The search result to validate
     * @param {boolean} allowsSearch - Whether search items are allowed
     * @param {string[]|null} [allowedRegionIds=null] - The current world's region ids (null = check the format only)
     * @returns {Object} Validation result with {valid, result?, error?, description?, droppedFields?}
     * @private
     */
    _validateSearchItem(result, allowsSearch, allowedRegionIds = null) {
        const description = `Search item "${this._previewItemName(result)}"`;
        if (!allowsSearch) {
            return { valid: false, error: `${description} not allowed for this searcher.` };
        }
        return this._validateListItemForImport(result, description, allowedRegionIds);
    }


    /**
     * Checks a search / pdf-lead / custom-pin item from the import dialog with the same field rules a
     * settings backup restore uses (_checkListItem), so an item the app exported comes back with every
     * field it was exported with; its isCustomized flag is then recomputed from its own user fields
     * (it is derived from them, so the file's copy is not trusted).
     * @param {Object} result - The imported item
     * @param {string} description - How the item is named in the report, e.g. 'Search item "x"'
     * @param {string[]|null} allowedRegionIds - The current world's region ids (null = check the format only)
     * @returns {Object} Validation result with {valid, result?, error?, description?, droppedFields?}
     * @private
     */
    _validateListItemForImport(result, description, allowedRegionIds) {
        const checked = this._checkListItem(result, allowedRegionIds);
        if (!checked.item) {
            return { valid: false, error: `${description} ${checked.reason}.` };
        }
        if (this.mapApp && typeof this.mapApp.updateItemIsCustomized === 'function') {
            this.mapApp.updateItemIsCustomized(checked.item);
        }
        return { valid: true, result: checked.item, description, droppedFields: checked.droppedFields };
    }


    /**
     * A short printable name for an item in a report: its user label, label, display name, query,
     * lead or uid, whichever it has first.
     * @param {Object} item - The item
     * @returns {string}
     * @private
     */
    _previewItemName(item) {
        if (!item || typeof item !== 'object') {
            return this._previewText(item);
        }
        for (const field of ['userLabel', 'label', 'dName', 'searchQuery', 'lead', 'uid']) {
            const value = item[field];
            if (typeof value === 'string' && value.trim()) {
                return this._previewText(value);
            }
        }
        // (a uid may be a number)
        return (typeof item.uid === 'number' && Number.isFinite(item.uid)) ? String(item.uid) : '';
    }


    /**
     * Checks one list item that is not a database POI -- a search, a pdf lead, a custom map pin or a
     * POI stub (see LIST_ITEM_KIND_FIELDS) -- for both the settings backup restore and the import
     * dialog. The item is refused if it lacks what its kind cannot do without (a uid; a searchable
     * search type; a pdf lead's lead; a custom pin's point). Otherwise every field the app writes on
     * that kind of item is KEPT UNCHANGED (same order too) when its value passes _checkListItemField,
     * and a field that fails or that the app never writes is left out and listed in droppedFields. The
     * values are user text or numbers: they stay plain strings and are escaped wherever they are shown.
     * @param {*} item - The stored or imported item
     * @param {string[]|null} allowedRegionIds - The current world's region ids (null = check the format only)
     * @returns {{item: Object|null, reason: string|null, droppedFields: Array<{field: string, reason: string}>}}
     *   item is null (and reason says why) when the whole item is refused
     * @private
     */
    _checkListItem(item, allowedRegionIds) {
        const refuse = (reason) => ({ item: null, reason, droppedFields: [] });
        if (!item || typeof item !== 'object' || Array.isArray(item)) {
            return refuse('is not an item object');
        }
        const kindFields = (typeof item.ptype === 'string') ? getOwnValue(LIST_ITEM_KIND_FIELDS, item.ptype) : undefined;
        if (!kindFields) {
            return refuse(`has an unknown type "${this._previewText(item.ptype)}"`);
        }
        if (!this._isValidImportUid(item.uid)) {
            return refuse('has a missing or invalid uid');
        }

        // what each kind of item cannot do without
        let cleanGeometry = null;
        if (item.ptype === PTYPES.SEARCH) {
            // it must name a searcher it can be re-run on, and its query must be text
            if (!IMPORT_SEARCH_TYPES.includes(item.searchType)) {
                return refuse(`has an unknown search type "${this._previewText(item.searchType)}" (expected one of ${IMPORT_SEARCH_TYPES.join(', ')})`);
            }
            if ((item.searchQuery !== undefined) && !this._isImportText(item.searchQuery, IMPORT_MAX_QUERY_LENGTH)) {
                return refuse(`has an invalid search query (not text, or longer than ${IMPORT_MAX_QUERY_LENGTH} characters)`);
            }
        } else if (item.ptype === PTYPES.PDFLEAD) {
            // the lead is looked up in the pdf outline and shown as the item's name, so it must be text
            if (!this._isImportText(item.lead, IMPORT_MAX_TEXT_LENGTH) || !item.lead.trim()) {
                return refuse(`has a missing or invalid lead (not text, empty, or longer than ${IMPORT_MAX_TEXT_LENGTH} characters)`);
            }
        } else if (item.ptype === PTYPES.CUSTOM) {
            // a pin the user dropped on the map: it is drawn at its point
            const geometry = item.geometry;
            const coords = geometry && geometry.coordinates;
            if (!geometry || geometry.type !== 'Point' || !Array.isArray(coords) || coords.length !== 2 || !coords.every(c => typeof c === 'number' && Number.isFinite(c))) {
                return refuse('has no valid point location');
            }
            cleanGeometry = { type: 'Point', coordinates: [coords[0], coords[1]] };
        }

        // every other field the app writes on this kind of item, kept as it is when it checks out
        const allowedFields = new Set([...LIST_ITEM_COMMON_FIELDS, ...kindFields]);
        const clean = {};
        const droppedFields = [];
        for (const [field, value] of Object.entries(item)) {
            if (value === undefined) {
                continue;
            }
            if (field === 'uid') {
                // (checked above) stored as TEXT: the result list reads a uid back from its card's
                // data-uid attribute and finds the item with ===, so a numeric uid could never be
                // clicked, opened or deleted
                clean.uid = String(value);
                continue;
            }
            if (field === 'ptype') {
                // (checked above)
                clean[field] = value;
                continue;
            }
            if (!allowedFields.has(field)) {
                droppedFields.push({ field, reason: 'not a field of this kind of item' });
                continue;
            }
            if (field === 'geometry') {
                // (checked above; rebuilt so nothing but the point rides along)
                clean.geometry = cleanGeometry;
                continue;
            }
            const outcome = this._checkListItemField(field, value, allowedRegionIds);
            if (outcome.ok) {
                clean[field] = outcome.value;
            } else {
                droppedFields.push({ field, reason: outcome.reason });
            }
        }
        // the values a kind of item always has
        if (item.ptype === PTYPES.SEARCH && clean.searchQuery === undefined) {
            clean.searchQuery = '';
        }
        if (item.ptype === PTYPES.CUSTOM) {
            clean.pcat = 'custom';
            clean.pcatg = 'custom';
        }
        return { item: clean, reason: null, droppedFields };
    }


    /**
     * Checks one field value of a list item (see _checkListItem): the type, size and, where the app
     * only ever writes a few values, the allowed value. Fails closed for a field with no rule here.
     * @param {string} field - The field name
     * @param {*} value - Its value
     * @param {string[]|null} allowedRegionIds - The current world's region ids (null = check the format only)
     * @returns {{ok: boolean, value?: *, reason?: string}} ok with the value to keep, or the reason it is left out
     * @private
     */
    _checkListItemField(field, value, allowedRegionIds) {
        const ok = (kept = value) => ({ ok: true, value: kept });
        const bad = (reason) => ({ ok: false, reason });
        const text = (max) => (this._isImportText(value, max) ? ok() : bad(`not text of at most ${max} characters`));
        switch (field) {
            // free text and image paths: strings of a sane length (shown escaped, or looked up by name)
            case 'userLabel':
            case 'userPin':
            case 'userStyle':
            case 'label':
            case 'dName':
            case 'dNameCustom':
            case 'customBase':
            case 'address':
            case 'lead':
            case 'loclabel':
            case 'jregion':
                return text(IMPORT_MAX_TEXT_LENGTH);
            case 'userNotes':
                return text(IMPORT_MAX_NOTES_LENGTH);
            case 'searchQuery':
                return text(IMPORT_MAX_QUERY_LENGTH);
            // colours end up in style attributes and in the bookmark dialog's <input type="color">,
            // which takes ONLY #rrggbb and turns anything else into black (so editing a pin imported
            // as "red" or "#f00" turned it black). So every colour is normalised to #rrggbb FIRST --
            // #rgb .. #rrggbbaa, a css colour name, and the color(srgb ...) value Safari's colour
            // picker gave while it carried `alpha` -- and only that form is kept
            case 'userColor': {
                const color = normalizeCssColorToHex(value);
                return (typeof color === 'string' && IMPORT_COLOR_REGEX.test(color)) ? ok(color) : bad('not a colour');
            }
            // scale: a percentage inside the bookmark dialog's size slider (a numeric string is
            // converted). The map multiplies it straight into the pin and callout size, so a huge
            // value made one pin cover the map; the slider has always had this range, so the app's
            // own exports never carry anything outside it
            case 'userScale': {
                const scale = (typeof value === 'string' && value.trim() !== '') ? Number(value) : value;
                const { MIN, MAX } = MAP_CUSTOMS.PIN_ICON_SCALE;
                return (typeof scale === 'number' && Number.isFinite(scale) && scale >= MIN && scale <= MAX) ? ok(scale) : bad(`not a size from ${MIN} to ${MAX}`);
            }
            // switches
            case 'userLook':
            case 'userCallout':
            case 'regionCheckbox':
            case FIELD_ISCUSTOMIZED:
            case FIELD_ISBOOKMARK:
                return (typeof value === 'boolean') ? ok() : bad('not true/false');
            case 'timeStampB':
            case 'timeStampH':
                return (typeof value === 'number' && Number.isFinite(value)) ? ok() : bad('not a time');
            // a search is re-run on its searcher and region, so those must be ones that exist
            case 'searchType':
                return IMPORT_SEARCH_TYPES.includes(value) ? ok() : bad(`not one of ${IMPORT_SEARCH_TYPES.join(', ')}`);
            case 'searchTotalCount':
                return (value === null || (typeof value === 'number' && Number.isFinite(value) && value >= -1)) ? ok() : bad('not a result count');
            case 'selectedRegion':
                return (value === '' || this._isValidImportRegion(value, allowedRegionIds)) ? ok() : bad(allowedRegionIds ? 'not a region of this world' : 'not a region id');
            // a custom pin's category is always "custom"
            case 'pcat':
            case 'pcatg':
                return (value === 'custom') ? ok() : bad('not "custom"');
            default:
                return bad('no check is defined for this field');
        }
    }


    /**
     * Whether a value is a string of at most maxLength characters.
     * @private
     */
    _isImportText(value, maxLength) {
        return (typeof value === 'string') && (value.length <= maxLength);
    }


    /**
     * Whether an imported selectedRegion is acceptable: one of the current world's region ids when
     * those are known, otherwise a well-formed region id.
     * @param {*} region - The imported value
     * @param {string[]|null} allowedRegionIds - The current world's region ids, or null
     * @returns {boolean}
     * @private
     */
    _isValidImportRegion(region, allowedRegionIds) {
        if (typeof region !== 'string') {
            return false;
        }
        if (allowedRegionIds && allowedRegionIds.length > 0) {
            return allowedRegionIds.includes(region);
        }
        return IMPORT_REGION_ID_REGEX.test(region);
    }


    /**
     * The region ids of the world loaded right now, or null if none are loaded.
     * @returns {string[]|null}
     * @private
     */
    _currentWorldRegionIds() {
        const auxDataManager = this.mapApp.getAuxDataManager ? this.mapApp.getAuxDataManager() : null;
        const regionData = auxDataManager ? auxDataManager.getRegionData() : null;
        if (!Array.isArray(regionData) || regionData.length === 0) {
            return null;
        }
        return regionData.map(region => region.jregion).filter(id => typeof id === 'string' && id);
    }


    /**
     * A short printable preview of an imported value for diagnostics (the report is plain text).
     * @private
     */
    _previewText(value) {
        const text = (value === null || value === undefined) ? '' : String(value);
        return (text.length > 80) ? text.slice(0, 80) + '…' : text;
    }


    /**
     * Validates a POI-type import item.
     * @param {Object} result - The POI result to validate
     * @param {boolean} allowsPoi - Whether POI items are allowed
     * @returns {Object} Validation result with {valid, result?, error?}
     * @private
     */
    _validatePoiItem(result, allowsPoi) {
        if (!allowsPoi) {
            return { valid: false, error: `POI item "${result.lead}" not allowed for this searcher.` };
        }

        // NEW, treat the imported POI item like a stub, that is, we ONLY care about the custom user fields and drop the rest and link to the real uid item
        // Look up the result in the poiDataManager by the lead field
        const foundItem = this.getPoiDataManager().findItemByLead(result.lead);
        if (!foundItem) {
            // not found, so REJECT and show a feedback message
            // ATTN: TODO - ALTERNATIVELY if we wanted to we could import it in as a poiStub and just show it in results even though it couldnt be acted on
            let extraInfo = '';
            if (result.label) {
                extraInfo = ` "${result.label}"`;
            } else if (result.dName) {
                extraInfo = ` "${result.dName}"`;
            }
            // JSON imports carry no line number, only the text scan does
            const lineInfo = (result.lineNo !== undefined) ? ` (line #${result.lineNo})` : '';
            return { valid: false, error: `${result.lead}${extraInfo}: Error, not found in database${lineInfo}.` };
        }

        // ok NOW, we have matched to a POI in the db -- the import adds a REFERENCE to that live
        // item, with the file's custom fields (after dropping values of the wrong shape: a file can
        // carry anything). Those fields are NOT written onto the live item here: validation runs on
        // every keystroke of the import box, and a preview the user then cancels must leave the map
        // as it was. They ride along as importedFields and importResultsIntoSearcher applies them.
        const droppedFields = [];
        const importedFields = this._sanitizeImportedCustomFields(result, droppedFields);

        return { valid: true, result: foundItem, importedFields, description: this._previewText(result.lead), droppedFields };
    }



    /**
     * Validates a pdf-lead import item (a bookmark of the case pdf that matched no POI).
     * @param {Object} result - The imported item
     * @param {boolean} allowsPoi - Whether this searcher takes such items
     * @param {string[]|null} [allowedRegionIds=null] - The current world's region ids (null = check the format only)
     * @returns {Object} Validation result with {valid, result?, error?, description?, droppedFields?}
     * @private
     */
    _validatePdfLead(result, allowsPoi, allowedRegionIds = null) {
        const description = `PDF Lead item "${this._previewText(result.lead)}"`;
        if (!allowsPoi) {
            return { valid: false, error: `${description} not allowed for this searcher.` };
        }
        return this._validateListItemForImport(result, description, allowedRegionIds);
    }


    /**
     * Validates a custom map pin import item (a bookmark the user dropped on the map; it matches no
     * POI, so it is imported whole, at its stored point).
     * @param {Object} result - The imported item
     * @param {boolean} allowsPoi - Whether this searcher takes such items
     * @returns {Object} Validation result with {valid, result?, error?, description?, droppedFields?}
     * @private
     */
    _validateCustomPin(result, allowsPoi) {
        const description = `Custom map pin "${this._previewItemName(result)}"`;
        if (!allowsPoi) {
            return { valid: false, error: `${description} not allowed for this searcher.` };
        }
        return this._validateListItemForImport(result, description, null);
    }


    /**
     * Whether an imported item's uid is usable: a non-empty string (or a finite number) of sane length.
     * Rendering escapes uids anyway; this keeps junk out of the stored lists. A numeric uid is accepted
     * but stored as its string (_checkListItem), since the lists match uids as strings.
     * @param {*} uid - The imported uid
     * @returns {boolean}
     * @private
     */
    _isValidImportUid(uid) {
        if (typeof uid === 'number') {
            return Number.isFinite(uid);
        }
        return (typeof uid === 'string') && (uid.trim().length > 0) && (uid.length <= IMPORT_MAX_UID_LENGTH);
    }


    /**
     * Returns a copy of an imported item with its custom bookmark fields checked: a value of the wrong
     * shape (a colour that is not a colour, a scale that is not a number, a label that is not text...)
     * is dropped, so the item falls back to the default for it. The values end up in style and value
     * attributes of the bookmark dialog and the map styles, and an imported file can contain anything.
     * The checks are _checkListItemField's, the same ones a stored list item's fields get.
     * @param {Object} result - The imported item
     * @param {Array<{field: string, reason: string}>} [droppedFields] - Collects the values left out
     * @returns {Object} A copy with the invalid custom fields removed
     * @private
     */
    _sanitizeImportedCustomFields(result, droppedFields = null) {
        const clean = { ...result };
        for (const field of CUSTOM_ITEM_FIELDS_ALL) {
            if (clean[field] === undefined) {
                continue;
            }
            const outcome = this._checkListItemField(field, clean[field], null);
            if (outcome.ok) {
                clean[field] = outcome.value;
            } else {
                delete clean[field];
                if (droppedFields) {
                    droppedFields.push({ field, reason: outcome.reason });
                }
            }
        }
        return clean;
    }



    // ==========================================
    // SETTINGS BACKUP (Settings > Import all settings)
    // ==========================================


    /**
     * Checks one item of a stored bookmarks / history / saved-search list, as found in a settings
     * backup. Such a backup may hold the lists of every world, so items cannot be checked against the
     * loaded POI database: they are checked by shape, with the same rules the import dialog applies to
     * the same kinds of item (_checkListItem). A stored list holds no database POI items -- those are
     * stored as POI stubs (MapApp.createItemCustomStubForSerializing) -- so an item is a POI stub, a
     * search, a pdf lead or a custom map pin, and anything else is refused. An accepted item keeps
     * every field the app writes on it, unchanged; what is left out is listed in droppedFields.
     * @param {*} item - The stored item
     * @returns {{item: Object|null, reason: string|null, droppedFields: Array<{field: string, reason: string}>}}
     */
    checkStoredListItem(item) {
        return this._checkListItem(item, null);
    }


    /**
     * The world ids of the loaded app config (its worlds dictionary's own keys), or null if no app
     * config is loaded.
     * @returns {string[]|null}
     * @private
     */
    _knownWorldIds() {
        const appConfig = this.mapApp ? this.mapApp.appConfig : null;
        const worlds = appConfig ? appConfig.worlds : null;
        if (!worlds || typeof worlds !== 'object' || Array.isArray(worlds)) {
            return null;
        }
        return Object.keys(worlds);
    }


    /**
     * Validates the entries of a settings backup (Settings > Import all settings) before any of them
     * is written to localStorage. Only this app's own keys are accepted, and only with values of the
     * shape the app itself writes:
     *  - app-scoped keys: lastWorldId (a world of this map), the background music lists and switches,
     *    the pdf highlights store,
     *  - world-scoped keys "<world prefix>[_<case>]_<ui|bookmarks|history|savedsearch>": the ui state
     *    (rebuilt from its known fields) and the three item lists (each item checked as above).
     * Everything the app writes is restored unchanged. Everything else -- other sites' or other apps'
     * keys, unknown keys under our prefix, values of the wrong shape -- is refused (a whole entry) or
     * left out (an item, a field or a value inside an accepted entry), and both are reported by name.
     * @param {Object} data - The backup's data section (key -> parsed value or raw string)
     * @param {string} appPrefix - The app storage prefix (e.g. 'cbmap:')
     * @returns {{accepted: Array<{key: string, value: string}>, refused: Array<{key: string, reason: string}>, dropped: Array<{key: string, what: string}>, droppedItemCount: number}}
     *   accepted entries carry the exact string to store; dropped lists what is left out of accepted
     *   entries, at most BACKUP_MAX_REPORTED_PER_KEY lines per key (droppedItemCount counts them all)
     */
    validateSettingsBackup(data, appPrefix) {
        const accepted = [];
        const refused = [];
        const dropped = [];
        const refuse = (key, reason) => refused.push({ key, reason });
        const parseIfString = (value) => {
            if (typeof value !== 'string') return value;
            try { return JSON.parse(value); } catch (e) { return undefined; }
        };
        const isText = (v, max) => this._isImportText(v, max);
        // one key's left-out values: the first BACKUP_MAX_REPORTED_PER_KEY by name, then a count
        const makeDropper = (key) => {
            let count = 0;
            return {
                drop: (what) => {
                    count++;
                    if (count <= BACKUP_MAX_REPORTED_PER_KEY) {
                        dropped.push({ key, what });
                    }
                },
                finish: () => {
                    if (count > BACKUP_MAX_REPORTED_PER_KEY) {
                        dropped.push({ key, what: `... and ${count - BACKUP_MAX_REPORTED_PER_KEY} more`, extraCount: count - BACKUP_MAX_REPORTED_PER_KEY - 1 });
                    }
                },
            };
        };
        const knownWorldIds = this._knownWorldIds();

        for (const [key, rawValue] of Object.entries(data || {})) {
            if (typeof key !== 'string' || !key.startsWith(appPrefix)) {
                refuse(key, 'not a key of this app');
                continue;
            }
            const relKey = key.slice(appPrefix.length);

            // --- app-scoped keys ---
            if (relKey === 'lastWorldId') {
                // stored as a raw string (the export keeps it as such); it must name a world of this
                // map's app config -- an OWN key of its worlds, so never "constructor" or "__proto__"
                // (startup used to look those up on the prototype and fail on every load)
                const worldId = rawValue;
                const isKnownWorld = (typeof worldId === 'string') && (knownWorldIds
                    ? knownWorldIds.includes(worldId)
                    : (BACKUP_WORLD_ID_REGEX.test(worldId) && !(worldId in Object.prototype)));
                if (isKnownWorld) {
                    accepted.push({ key, value: worldId });
                } else {
                    refuse(key, knownWorldIds ? 'not one of the worlds of this map' : 'not a world id');
                }
                continue;
            }
            if (relKey === 'bgMusicStayAndLoop') {
                const value = parseIfString(rawValue);
                if (typeof value === 'boolean') {
                    accepted.push({ key, value: JSON.stringify(value) });
                } else {
                    refuse(key, 'not true/false');
                }
                continue;
            }
            if (relKey === 'bgMusicActivePlaylist') {
                // a JSON string; the export may hand it back already parsed (a bare string)
                const value = (typeof rawValue === 'string' && rawValue.startsWith('"')) ? parseIfString(rawValue) : rawValue;
                if (isText(value, IMPORT_MAX_QUERY_LENGTH)) {
                    accepted.push({ key, value: JSON.stringify(value) });
                } else {
                    refuse(key, 'not a playlist id');
                }
                continue;
            }
            if (relKey === 'bgMusicUserTracks' || relKey === 'bgMusicPlaylists') {
                // (the same entry rule StateManager applies whenever it reads these lists back)
                const textField = (relKey === 'bgMusicUserTracks') ? 'title' : 'label';
                const result = sanitizeStoredMusicList(parseIfString(rawValue), textField, { maxUrlLength: IMPORT_MAX_QUERY_LENGTH, maxTextLength: IMPORT_MAX_TEXT_LENGTH });
                if (!result) {
                    refuse(key, 'not a list');
                    continue;
                }
                const dropper = makeDropper(key);
                for (const { index, field } of result.dropped) {
                    if (field === null) {
                        dropper.drop(`entry ${index + 1}: not a ${textField} and url`);
                    } else {
                        dropper.drop(`entry ${index + 1}: left out "${this._previewText(field)}" (not a field of a ${textField === 'title' ? 'track' : 'playlist'})`);
                    }
                }
                dropper.finish();
                accepted.push({ key, value: JSON.stringify(result.list) });
                continue;
            }
            if (key === PdfHighlightStore.STORAGE_KEY) {
                const result = PdfHighlightStore.sanitizeStoreValue(parseIfString(rawValue));
                if (result) {
                    if (result.droppedCount > 0) {
                        dropped.push({ key, what: `${result.droppedCount} highlight${result.droppedCount === 1 ? '' : 's'} or pdf entr${result.droppedCount === 1 ? 'y' : 'ies'} that are not well-formed`, extraCount: result.droppedCount - 1 });
                    }
                    accepted.push({ key, value: JSON.stringify(result.store) });
                } else {
                    refuse(key, 'not a pdf highlights store');
                }
                continue;
            }

            // --- world-scoped keys ---
            if (relKey.includes('__corrupt_')) {
                // StateManager._handleCorruptStoredState keeps a copy of stored state it could not read
                refuse(key, 'a copy of saved state that could not be read (kept for inspection only)');
                continue;
            }
            const match = relKey.match(BACKUP_WORLD_KEY_REGEX);
            if (!match) {
                refuse(key, 'unknown key');
                continue;
            }
            const suffix = match[2];
            const value = parseIfString(rawValue);
            if (suffix === 'ui') {
                const result = sanitizeStoredUiState(value);
                if (result) {
                    const dropper = makeDropper(key);
                    for (const { path, reason } of result.dropped) {
                        dropper.drop(`${path}: ${reason}`);
                    }
                    for (const path of result.unknown) {
                        dropper.drop(`${this._previewText(path)}: not a setting of this app`);
                    }
                    dropper.finish();
                    accepted.push({ key, value: JSON.stringify(result.state) });
                } else {
                    refuse(key, 'not a settings object');
                }
                continue;
            }
            // bookmarks / history / savedsearch lists
            if (!Array.isArray(value)) {
                refuse(key, 'not a list');
                continue;
            }
            const dropper = makeDropper(key);
            const cleanItems = [];
            value.forEach((item, index) => {
                const checked = this.checkStoredListItem(item);
                const name = this._previewItemName(item);
                const itemLabel = `item ${index + 1}${name ? ` ("${name}")` : ''}`;
                if (!checked.item) {
                    dropper.drop(`${itemLabel} ${checked.reason}`);
                    return;
                }
                cleanItems.push(checked.item);
                for (const { field, reason } of checked.droppedFields) {
                    dropper.drop(`${itemLabel}: left out "${this._previewText(field)}" (${reason})`);
                }
            });
            dropper.finish();
            accepted.push({ key, value: JSON.stringify(cleanItems) });
        }

        // droppedItemCount counts every left-out value, including those summarized as "... and N more"
        const droppedItemCount = dropped.reduce((sum, d) => sum + 1 + (d.extraCount || 0), 0);
        return { accepted, refused, dropped, droppedItemCount };
    }



    /**
     * Builds the final validation diagnostics message.
     * @param {Array<Object>} validatedResults - Array of validated results
     * @param {Array<string>} errors - Array of error messages
     * @param {string} label - Label for the import target
     * @param {Array<string>} [warnings=[]] - Values left out of items that ARE imported
     * @returns {Object} Result object with {results, diagnosticsMessage, success}
     * @private
     */
    _buildValidationDiagnostics(validatedResults, errors, label, warnings = []) {
        const goodCount = validatedResults.length;
        const badCount = errors.length;

        const goodResultTextSummary = this.buildImportValidationSummaryText(validatedResults);
        const badResultTextSummary = this.buildImportValidationSummaryText(errors);

        if (goodCount > 0) {
            let message = `Found ${goodCount} valid item${goodCount !== 1 ? 's' : ''} ready to import to ${label}`;
            if (badCount > 0) {
                message += ` (${badCount} item${badCount !== 1 ? 's' : ''} will be skipped)`;
            }

            // summarize what is about to happen
            message += `:\n` + goodResultTextSummary;
            if (badCount > 0) {
                message += `\n\nSkipped (errored) items:\n` + badResultTextSummary;
            }
            if (warnings.length > 0) {
                message += `\n\nLeft out of the imported items (${warnings.length} invalid or unknown value${warnings.length !== 1 ? 's' : ''}):\n` + this.buildImportValidationSummaryText(warnings);
            }

            // return it
            return createAnnotatedReturnVal(validatedResults, message, true);
        }

        if (badCount > 0) {
            const message = `ERROR: No valid items found. ${badCount} item${badCount !== 1 ? 's' : ''} failed validation:\n` + badResultTextSummary;
            return createAnnotatedReturnVal([], message, false);
        }

        return createAnnotatedReturnVal([], 'ERROR: No items to validate.', false);
    }


    /**
     * Builds a human-readable summary text from a list of validation results or error strings.
     * @param {Array<Object|string>} validationList - Array of validated items or error strings
     * @returns {string} Formatted summary text
     */
    buildImportValidationSummaryText(validationList) {
        let text = '';
        for (const item of validationList) {
            if (typeof item === 'string') {
                text += item + '\n';
            } else if (item && item.lead) {
                let extra = '';
                if (item.label) {
                    extra += ` "${item.label}"`
                }
                if (item.userLabel) {
                    extra = ` "${item.userLabel}"`
                }
                text += `${item.lead}: ${item.dName}${extra}\n`;
            }
        }
        return text;
    }


    /**
     * Converts JSON data to export text in the specified format.
     * @param {string} formatType - Output format ('json', 'casebook', 'plaintext')
     * @param {string} tabName - Tab/searcher name for context
     * @param {string} label - Label for the export
     * @param {Array<Object>} data - Array of items to export
     * @returns {string} Formatted text output
     */
    parseJsonProduceExportText(formatType, tabName, label, data) {
        let retv;
        if (formatType=='json') {
            retv = JSON.stringify(data, null, 2);
        } else {
            retv = data.map(item => this.parseJsonProduceExportTextForItem(formatType, item)).join('\n');
        }
        return retv;
    }


    /**
     * Converts a single item to export text in the specified format.
     * @param {string} formatType - Output format ('json', 'casebook', 'plaintext')
     * @param {Object} item - The item to format
     * @returns {string} Formatted text for the item
     */
    parseJsonProduceExportTextForItem(formatType, item) {
        if (item.ptype && item.ptype==PTYPES.SEARCH) {
            const searchType = String(item.searchType || '').toUpperCase();
            const searchQuery = item.searchQuery;
            // (saved and history searches store their count as searchTotalCount, IntSearcher.
            // addSavedSearchItemManually; -1 or none = no meaningful count, as in the Lister)
            const totalCount = item.searchTotalCount;
            const countText = (typeof totalCount === 'number' && Number.isFinite(totalCount) && totalCount >= 0) ? ` (${totalCount} total results)` : '';
            return `// ${searchType} SEARCH: "${searchQuery}"${countText}` + ((formatType=='casebook') ? '\n' : '');
        }

        if (formatType=='casebook') {
            return `## ${item.lead} "${item.dName}"\n// ${formatAddressAptWithComma(item.address, item.apt)}${formatJregionLoclabelWithAt(item.jregion, item.loclabel)}\n`;
        }
        if (formatType=='plaintext') {
            return `[${item.lead}] ${item.dName} - ${formatAddressAptWithComma(item.address, item.apt)}${formatJregionLoclabelWithAt(item.jregion, item.loclabel)}`;
        }
        if (formatType=='justLeads') {
            return `${item.lead}`;
        }
        return `Unknown item type.`;
    }




    // ==========================================
    // IMPORT EXPORT INTERACTIVE HELPERS
    // ==========================================


    /**
     * Validates import text for the import dialog's report box (no import happens).
     * Failures are flagged explicitly and their text made to start with "ERROR: ", so the
     * dialog never has to guess from the wording; a successful report legitimately contains
     * the word "error" whenever it lists skipped items.
     * @param {string} tabName - Target tab/searcher name for import
     * @param {string} label - Display label for diagnostic messages
     * @param {string} text - Raw text content to validate
     * @returns {{message: string, isError: boolean}} Report text and whether it is a failure
     */
    buildImportDialogDiagnostics(tabName, label, text) {
        const resultsWithMessage = this.parseTextForImport(tabName, label, text, true);
        let message = resultsWithMessage.diagnosticsMessage || '';
        if (!resultsWithMessage.success && !/^\s*error\b/i.test(message)) {
            message = 'ERROR: ' + message;
        }
        return { message, isError: !resultsWithMessage.success };
    }


    /**
     * Opens the import dialog and handles interactive data import.
     * @async
     * @param {string} tabName - Target tab/searcher name for import
     * @param {string} label - Display label for the import dialog
     * @returns {Promise<boolean>} True if import was successful
     */
    async importInteractively(tabName, label, flagSupportsImportLeadsFromPdf) {
        const dialogOptions = {
            title: `Import ${label}`,
            //infoText: 'From here you can import previously exported data.',
            importButtonText: 'Import data now',
            acceptFileTypes: '.txt,.json,.casebook',
            flagSupportsImportLeadsFromPdf: flagSupportsImportLeadsFromPdf,
            getPdfLeads: async () => {
                return await this.mapApp.getPdfLeads();
            },
            contentParser: async (text) => this.buildImportDialogDiagnostics(tabName, label, text)
        };

        // show it
        const textToImport = await ImportDialog.showImportDialog(dialogOptions);
        if (textToImport) {
            const resultsWithMessage = this.parseTextForImport(tabName, label, textToImport, false);
            if (!resultsWithMessage.success) {
                alert(`IMPORT INTO ${tabName} DATA FAILED: ${resultsWithMessage.diagnosticsMessage}.`)
            }
            // note that refresh is called in parseTextForImport() so we dont need to do it again
            return resultsWithMessage.success;
        }
    }




    /**
     * Opens the export dialog and handles interactive data export.
     * @async
     * @param {string} tabName - Source tab/searcher name
     * @param {string} label - Display label for the export dialog
     * @param {string} defaultFilename - Default filename without extension
     * @param {Array<Object>} dataAsJson - Data to export as JSON array
     * @param {string} defaultFormat - Default export format ('json', 'casebook', 'plaintext')
     * @returns {Promise<void>}
     */
    async exportInteractively(tabName, label, defaultFilename, dataAsJson, defaultFormat) {
        // Use saved format if available, otherwise use the provided default
        const stateManager = this.mapApp.getStateManager();
        const savedFormat = stateManager.getLastExportFormat();
        const formatToUse = savedFormat || defaultFormat || 'json';

        const itemCount = dataAsJson ? dataAsJson.length : 0;

        const dialogOptions = {
            contentJson: dataAsJson,
            title: `Export ${label} (${itemCount} items)`,
            //infoText: 'From here you can export data for other purposes.',
            defaultFilename: defaultFilename,
            defaultFormat: formatToUse,
            formatConverters: {
                json: {
                    label: 'JSON',
                    fileExtension: '.json',
                    mimeType: 'application/json',
                    converter: (data) => this.parseJsonProduceExportText('json', tabName, label, data)
                },
                casebook: {
                    label: 'Casebook Text',
                    fileExtension: '.txt',
                    converter: (data) => this.parseJsonProduceExportText('casebook', tabName, label, data)
                },
                plaintext: {
                    label: 'Plain Text',
                    fileExtension: '.txt',
                    converter: (data) => this.parseJsonProduceExportText('plaintext', tabName, label, data)
                },
                justLeads: {
                    label: 'Just Leads',
                    fileExtension: '.txt',
                    converter: (data) => this.parseJsonProduceExportText('justLeads', tabName, label, data)
                },
            }
        };

        // show it
        const result = await ExportDialog.showExportDialog(dialogOptions);
        if (result.format) {
            // Save the last used format
            stateManager.setLastExportFormat(result.format);
        }
        if (result.filename) {
            console.info(`CasebookMap exported ${label} to ${result.filename}.`);
        }
    }


}
